import React from "react";

export default function GameEventTimeline({
    events,
    loading,
    error,
    canEdit,
    onDelete,
    getEventConfig,
    getSide,
    getTimeLabel,
    renderEventBody,
    visitorName,
    homeName,
    timelineLabel,
    loadingContent,
    errorContent,
    emptyContent,
}) {
    const timelineEvents = events || [];

    return (
        <div className="kbola-gameEventTimeline">
            <div className="kbola-centralTimelineHeader">
                <span className="kbola-centralTimelineLabel is-left">{visitorName}</span>
                <span className="kbola-centralTimelineLabel is-center">{timelineLabel}</span>
                <span className="kbola-centralTimelineLabel is-right">{homeName}</span>
            </div>

            {loading ? (
                <div className="kbola-timelineEmpty">{loadingContent}</div>
            ) : error ? (
                <div className="kbola-timelineEmpty">{errorContent}</div>
            ) : timelineEvents.length === 0 ? (
                <div className="kbola-timelineEmpty">{emptyContent}</div>
            ) : (
                <div className="kbola-centralTimeline">
                    <div className="kbola-centralTimelineRail"/>
                    {timelineEvents.map((event) => {
                        const cfg = getEventConfig(event) || {};
                        const side = getSide(event) === "right" ? "right" : "left";

                        return (
                            <div
                                key={event.id}
                                className={`kbola-centralTimelineRow ${side === "right" ? "is-right" : "is-left"}`}
                            >
                                <div className="kbola-centralTimelineSide left">
                                    {side === "left" && (
                                        <TimelineEventCard
                                            event={event}
                                            cfg={cfg}
                                            side={side}
                                            canEdit={canEdit}
                                            onDelete={onDelete}
                                            renderEventBody={renderEventBody}
                                        />
                                    )}
                                </div>

                                <div className="kbola-centralTimelineMiddle">
                                    <div
                                        className="kbola-centralMinute"
                                        style={{
                                            "--evt-accent": cfg.accent || "#14151a",
                                            "--evt-glow": cfg.glow || "rgba(0,0,0,0.06)",
                                        }}
                                    >
                                        {getTimeLabel(event)}
                                    </div>
                                </div>

                                <div className="kbola-centralTimelineSide right">
                                    {side === "right" && (
                                        <TimelineEventCard
                                            event={event}
                                            cfg={cfg}
                                            side={side}
                                            canEdit={canEdit}
                                            onDelete={onDelete}
                                            renderEventBody={renderEventBody}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function TimelineEventCard({event, cfg, side, canEdit, onDelete, renderEventBody}) {
    return (
        <div
            className={`kbola-feedCard ${side === "right" ? "is-right" : "is-left"}`}
            style={{
                "--evt-accent": cfg.accent || "#14151a",
                "--evt-soft": cfg.soft || "rgba(0,0,0,0.06)",
                "--evt-glow": cfg.glow || "rgba(0,0,0,0.06)",
            }}
        >
            <div className="kbola-feedCardTop">
                <div className="kbola-feedCardTopLeft">
                    <div className={`kbola-feedTeamTag ${side === "right" ? "is-right" : "is-left"}`}>
                        {side === "right" ? "HOME" : "AWAY"}
                    </div>
                    <div className="kbola-feedType">
                        <span className="kbola-feedTypeIcon"><i className={cfg.icon || "fas fa-circle"}/></span>
                        <span className="kbola-feedTypeText">{cfg.label || event.actionType}</span>
                    </div>
                </div>
                {canEdit && (
                    <button type="button" className="btn kbola-feedDelete" onClick={() => onDelete(event.id)} title="Delete">
                        <i className="fas fa-trash"/>
                    </button>
                )}
            </div>
            <div className="kbola-feedBody">
                {renderEventBody(event, cfg, side)}
            </div>
        </div>
    );
}

export function GameEventTimelineStyles({scope = ".kbola-gameEventTimeline"}) {
    return (
        <style>{`
            ${scope} .kbola-centralTimelineHeader { display: grid; grid-template-columns: 1fr 92px 1fr; align-items: center; gap: 8px; margin-bottom: 12px; }
            ${scope} .kbola-centralTimelineLabel { color: rgba(20,21,26,.48); font-size: 11px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
            ${scope} .kbola-centralTimelineLabel.is-center { text-align: center; }
            ${scope} .kbola-centralTimelineLabel.is-right { text-align: right; }
            ${scope} .kbola-centralTimeline { position: relative; display: flex; flex-direction: column; gap: 18px; }
            ${scope} .kbola-centralTimelineRail { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; transform: translateX(-50%); background: linear-gradient(180deg, rgba(0,0,0,.03), rgba(0,0,0,.10) 12%, rgba(0,0,0,.10) 88%, rgba(0,0,0,.03)); }
            ${scope} .kbola-centralTimelineRow { position: relative; display: grid; grid-template-columns: minmax(0,1fr) 92px minmax(0,1fr); gap: 10px; align-items: center; }
            ${scope} .kbola-centralTimelineSide.left { display: flex; justify-content: flex-end; }
            ${scope} .kbola-centralTimelineSide.right { display: flex; justify-content: flex-start; }
            ${scope} .kbola-centralTimelineMiddle { position: relative; z-index: 2; display: flex; justify-content: center; }
            ${scope} .kbola-centralMinute { min-width: 56px; height: 56px; border: 2px solid var(--evt-accent); border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; background: #fff; color: rgba(20,21,26,.88); font-size: 13px; font-weight: 900; box-shadow: 0 8px 18px rgba(0,0,0,.06), 0 0 0 6px #fff, 0 0 0 8px var(--evt-glow); }
            ${scope} .kbola-feedCard { position: relative; width: 100%; max-width: 360px; overflow: hidden; padding: 14px 15px; border: 1px solid rgba(0,0,0,.07); border-radius: 20px; background: linear-gradient(180deg,#fff 0%,#f8fafc 100%); box-shadow: 0 12px 24px rgba(0,0,0,.05), 0 2px 8px rgba(0,0,0,.03); }
            ${scope} .kbola-feedCard::after { content: ""; position: absolute; top: 0; bottom: 0; width: 5px; background: var(--evt-accent); }
            ${scope} .kbola-feedCard.is-left::after { right: 0; }
            ${scope} .kbola-feedCard.is-right::after { left: 0; }
            ${scope} .kbola-feedCardTop { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
            ${scope} .kbola-feedCardTopLeft { display: flex; align-items: center; min-width: 0; gap: 8px; }
            ${scope} .kbola-feedTeamTag { padding: 4px 7px; border-radius: 999px; color: var(--evt-accent); background: var(--evt-soft); font-size: 9px; font-weight: 900; letter-spacing: .08em; }
            ${scope} .kbola-feedType { display: flex; align-items: center; min-width: 0; gap: 7px; }
            ${scope} .kbola-feedTypeIcon { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; flex: 0 0 28px; border-radius: 9px; color: var(--evt-accent); background: var(--evt-soft); }
            ${scope} .kbola-feedTypeText { overflow: hidden; color: rgba(20,21,26,.84); font-size: 12px; font-weight: 900; text-overflow: ellipsis; white-space: nowrap; }
            ${scope} .kbola-feedDelete { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; flex: 0 0 34px; padding: 0; border: 1px solid rgba(217,45,32,.20); border-radius: 10px; color: #d92d20; background: rgba(217,45,32,.08); }
            ${scope} .kbola-feedDelete:hover { color: #fff; background: #d92d20; }
            ${scope} .kbola-feedBody { min-width: 0; }
            ${scope} .kbola-feedHeroRow { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
            ${scope} .kbola-feedHeroPlayer { overflow: hidden; color: rgba(20,21,26,.90); font-size: 14px; font-weight: 900; text-overflow: ellipsis; white-space: nowrap; }
            ${scope} .kbola-feedMiniTag { padding: 4px 7px; border-radius: 999px; color: var(--evt-accent); background: var(--evt-soft); font-size: 9px; font-weight: 900; }
            ${scope} .kbola-feedLinked, ${scope} .kbola-feedNotes { margin-top: 8px; color: rgba(20,21,26,.56); font-size: 11px; font-weight: 800; }
            ${scope} .kbola-timelineEmpty { min-height: 180px; display: flex; align-items: center; justify-content: center; padding: 16px; border: 1px dashed rgba(0,0,0,.11); border-radius: 18px; color: rgba(20,21,26,.60); font-size: 13px; font-weight: 800; text-align: center; }
            @media (max-width: 700px) {
                ${scope} .kbola-centralTimelineHeader { grid-template-columns: 1fr 54px 1fr; }
                ${scope} .kbola-centralTimelineRow { grid-template-columns: minmax(0,1fr) 54px minmax(0,1fr); gap: 7px; }
                ${scope} .kbola-feedCard { padding: 10px; }
                ${scope} .kbola-feedTypeText { display: none; }
            }
        `}</style>
    );
}
